import { AnalyticsServiceProvider } from '@completeanatomy/analytics';
import { AuthProvider as CompleteAnatomyAuth } from '@completeanatomy/auth';
import { BridgeProvider } from '@completeanatomy/bridge';
import { AppStateProvider, registerBridge } from '@completeanatomy/common';
import { ConfigProvider } from '@completeanatomy/config';
import { EventBusProvider } from '@completeanatomy/event-bus';
import { SvgSprite } from '@completeanatomy/ui';
import { UserServiceProvider } from '@completeanatomy/user';
import React from 'react';
import ReactDOM from 'react-dom/client';
import { AuthProvider } from 'react-oidc-context';

import App from './App';
import { globalVariables } from './config/globalVariables';
import { NEXT_URL_STORAGE_KEY } from './pages/AuthCallback';
import bridge from './utils/Bridge';
import { kcGateway } from './utils/keycloak';
import { resolveLMSDeepLink } from './utils/LMSDeeplink';

registerBridge(bridge);

const root = ReactDOM.createRoot(
  document.getElementById('root') as HTMLElement
);

const isScreenPage =
  window.location.pathname.includes('/screens/') ||
  window.location.pathname.includes('/models/') ||
  window.location.pathname === '/';

if (isScreenPage) {
  sessionStorage.setItem(
    NEXT_URL_STORAGE_KEY,
    window.location.pathname + window.location.search
  );
}

if (!process.env.REACT_APP_KC_TOKEN) {
  const originalFetch = window.fetch;

  window.fetch = (input: RequestInfo | URL, init?: RequestInit) => {
    const redirectApi = [
      '/h/ca-web',
      '/api',
      '/v1',
      '/assets',
      '/complete_anatomy-userdata',
      '/3d4medical-235405227423-websdk-resources',
      '/api/v2/deeplinks',
    ];

    const currentUrl = input.toString();

    const isModifyRequired = redirectApi.some(api => {
      const isAnotherDomain = currentUrl.includes(api);
      const isContent = currentUrl.includes('/content/v1');
      const accounts = currentUrl.includes('accounts');
      const purchases = currentUrl.includes('/api/v1/purchases');
      const isAuth = currentUrl.includes('api/v13');
      const isCookie = currentUrl.includes('cookielaw');
      const isLMSRedirect = currentUrl.includes('/api/v2/deeplinks');

      return (
        isAnotherDomain &&
        !isContent &&
        !isAuth &&
        !isCookie &&
        !accounts &&
        !purchases &&
        !isLMSRedirect
      );
    });

    const isTexture = currentUrl.includes('/textures');

    const isLMSRedirect = currentUrl.includes('/api/v2/deeplinks');

    if (isModifyRequired) {
      return originalFetch(globalVariables.apiDomain + currentUrl, init);
    }
    if (isTexture) {
      return originalFetch(globalVariables.userData + currentUrl, init);
    }
    if (isLMSRedirect) {
      return originalFetch(globalVariables.eolsDomain + currentUrl, init);
    }

    return originalFetch(input, init);
  };
}

// Set up cookie banner
const oneTrustScript = document.createElement('script');

oneTrustScript.type = 'text/javascript';
oneTrustScript.src = globalVariables.cookieSrc;
document.head.appendChild(oneTrustScript);

const otSDKStubScript = document.createElement('script');

otSDKStubScript.type = 'text/javascript';
otSDKStubScript.src = 'https://cdn.cookielaw.org/scripttemplates/otSDKStub.js';
otSDKStubScript.setAttribute('charset', 'UTF-8');
otSDKStubScript.setAttribute('data-domain-script', globalVariables.cookieId);

document.head.appendChild(otSDKStubScript);

otSDKStubScript.onload = () => {
  //@ts-ignore
  if (window.Optanon && window.Optanon.Alert) {
    //@ts-ignore
    window.Optanon.Alert.showAlert();
  }
};

const renderApp = () => {
  root.render(
    <AuthProvider userManager={kcGateway.userManager}>
      <AppStateProvider>
        <BridgeProvider>
          <AnalyticsServiceProvider>
            <ConfigProvider>
              <CompleteAnatomyAuth>
                <EventBusProvider>
                  <UserServiceProvider>
                    <SvgSprite />
                    <App />
                  </UserServiceProvider>
                </EventBusProvider>
              </CompleteAnatomyAuth>
            </ConfigProvider>
          </AnalyticsServiceProvider>
        </BridgeProvider>
      </AppStateProvider>
    </AuthProvider>
  );
};

const initApp = async () => {
  const redirected = await resolveLMSDeepLink();

  if (!redirected) {
    renderApp();
  }
};

initApp();
